import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Deck as DeckIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
    getDecksWithArchetypes,
    getAllArchetypes,
    createDeck,
    updateDeck,
    deleteDeck,
    suggestArchetypeForDeck,
    saveArchetypeSuggestionFeedback,
    deleteDeckImage,
    getUserData,
    createOrUpdateUser
} from '../../services/tracker-limited-data';
import DeckForm from './DeckForm';
import DeckFilters from './DeckFilters';
import { findDuplicateDeck, suggestDeckNames } from './deck-utils';
import DeckGroupedList from './DeckGroupedList';
import ConfirmDialog from './ConfirmDialog';
import DeckPreviewModal from './DeckPreviewModal';

const FORMAT_LABELS = {
    core_constructed: 'Core Constructed',
    infinity: 'Infinity'
};

const normalizeSearchText = (value) =>
    String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

const buildDeckSearchHaystack = (deck) => {
    const visualNames = Array.isArray(deck?.deckVisual?.cards)
        ? deck.deckVisual.cards
            .map((entry) => `${entry?.name || ''} ${entry?.subtitle || ''}`.trim())
            .join('\n')
        : '';
    return normalizeSearchText([
        deck?.name || '',
        deck?.cards || '',
        visualNames
    ].join('\n'));
};

function DeckManager({ onOpenDialog, onCloseDialog, dialogOpen: externalDialogOpen, onOpenQuickMatch, onStatsUpdate, trackerFilters, trackerSetLabel = '', prefillDeckCards = null, onPrefillConsumed }) {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [decks, setDecks] = useState([]);
    const [archetypes, setArchetypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Synchronizuj dialogOpen z props
    React.useEffect(() => {
        if (externalDialogOpen !== undefined) {
            setDialogOpen(externalDialogOpen);
        }
    }, [externalDialogOpen]);

    // Otwórz dialog z pre-wypełnioną listą kart z duels.ink
    React.useEffect(() => {
        if (!prefillDeckCards) return;
        setEditingDeck(null);
        setFormData(prev => ({
            ...prev,
            name: '',
            archetype: '',
            cards: prefillDeckCards,
            dreamborn_url: '',
            image_url: '',
        }));
        setArchetypeSuggestions([]);
        setNameSuggestions([]);
        if (onOpenDialog) onOpenDialog();
        else setDialogOpen(true);
        if (onPrefillConsumed) onPrefillConsumed();
    }, [prefillDeckCards]); // eslint-disable-line react-hooks/exhaustive-deps

    const [editingDeck, setEditingDeck] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        archetype: '',
        privacy: 'internal',
        cards: '',
        dreamborn_url: '',
        image_url: '',
        format: trackerFilters?.format || 'core_constructed',
        setId: trackerFilters?.setId || ''
    });
    const [archetypeFilter, setArchetypeFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [userDeckCategories, setUserDeckCategories] = useState({});
    const [userFavoriteDecks, setUserFavoriteDecks] = useState(new Set());
    const [viewMode, setViewMode] = useState('list'); // 'list' lub 'grid'
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [showMyDecksOnly, setShowMyDecksOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedArchetypes, setExpandedArchetypes] = useState(new Set());
    const [suggestingArchetype, setSuggestingArchetype] = useState(false);
    const [archetypeSuggestions, setArchetypeSuggestions] = useState([]);
    const [suggestingName, setSuggestingName] = useState(false);
    const [nameSuggestions, setNameSuggestions] = useState([]);
    const [confirmDeleteDeck, setConfirmDeleteDeck] = useState({ open: false, deckId: null, deckName: '' });
    const [previewDeck, setPreviewDeck] = useState(null);

    // Ładowanie danych przy inicjalizacji
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [userDecksData, internalDecksData, archetypesData, userData] = await Promise.all([
                    currentUser ? getDecksWithArchetypes(currentUser.uid, trackerFilters) : [],
                    getDecksWithArchetypes(null, trackerFilters), // Talie wewnętrzne
                    getAllArchetypes(trackerFilters),
                    currentUser ? getUserData(currentUser.uid) : null
                ]);

                // Połącz talie użytkownika z wewnętrznymi, usuwając duplikaty
                const allDecks = [...userDecksData];
                const userDeckIds = new Set(userDecksData.map(deck => deck.id));

                internalDecksData.forEach(internalDeck => {
                    if (!userDeckIds.has(internalDeck.id)) {
                        allDecks.push(internalDeck);
                    }
                });

                setDecks(allDecks);
                setArchetypes(archetypesData);

                // Załaduj kategorie użytkownika
                if (userData && userData.deckCategories) {
                    setUserDeckCategories(userData.deckCategories);
                } else {
                    setUserDeckCategories({});
                }

                // Załaduj ulubione talie użytkownika
                if (userData && userData.favoriteDecks) {
                    setUserFavoriteDecks(new Set(userData.favoriteDecks));
                } else {
                    setUserFavoriteDecks(new Set());
                }

            } catch (err) {
                console.error('Błąd podczas ładowania danych:', err);
                setError('Nie udało się załadować talii. Spróbuj ponownie.');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [currentUser, trackerFilters]);

    const handleOpenDialog = (deck = null) => {
        if (deck) {
            setEditingDeck(deck);
            setFormData({
                name: deck.name,
                archetype: deck.archetype || '',
                privacy: 'internal',
                cards: deck.cards || '',
                dreamborn_url: deck.dreamborn_url || '',
                image_url: deck.image_url || '',
                format: deck.format || trackerFilters?.format || 'core_constructed',
                setId: deck.setId || trackerFilters?.setId || ''
            });
        } else {
            setEditingDeck(null);
            setFormData({
                name: '',
                archetype: '',
                privacy: 'internal',
                cards: '',
                dreamborn_url: '',
                image_url: '',
                format: trackerFilters?.format || 'core_constructed',
                setId: trackerFilters?.setId || ''
            });
        }
        setArchetypeSuggestions([]);
        setNameSuggestions([]);
        if (onOpenDialog) {
            onOpenDialog();
        } else {
            setDialogOpen(true);
        }
    };

    const handleCloseDialog = () => {
        if (onCloseDialog) {
            onCloseDialog();
        } else {
            setDialogOpen(false);
        }
        setEditingDeck(null);
        setFormData({
            name: '',
            archetype: '',
            privacy: 'internal',
            cards: '',
            dreamborn_url: '',
            image_url: '',
            format: trackerFilters?.format || 'core_constructed',
            setId: trackerFilters?.setId || ''
        });
        setArchetypeSuggestions([]);
        setNameSuggestions([]);
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (field === 'archetype' && value && formData.cards?.trim()) {
            setNameSuggestions(suggestDeckNames(formData.cards, value, decks, archetypes));
        }
        if (field === 'archetype' && !value) {
            setNameSuggestions([]);
        }
    };

    const handleSubmit = async () => {
        if (!currentUser) {
            setError('Musisz być zalogowany, aby dodawać talii.');
            return;
        }

        // Walidacja wymaganych pól
        if (!formData.name.trim()) {
            setError('Nazwa talii jest wymagana.');
            return;
        }
        if (!formData.archetype) {
            setError('Archetyp jest wymagany.');
            return;
        }
        if (!formData.cards.trim()) {
            setError('Lista kart jest wymagana.');
            return;
        }
        try {
            setError(null);

            const deckData = {
                ...formData,
                // Zachowaj oryginalnego autora podczas edycji, ustaw nowego autora tylko dla nowych talii
                author: editingDeck ? editingDeck.author : currentUser.uid
            };

            if (!editingDeck) {
                deckData.format = trackerFilters?.format || 'core_constructed';
                deckData.setId = trackerFilters?.setId || '';
            }

            if (!editingDeck) {
                const duplicate = findDuplicateDeck(formData.cards, decks, formData.archetype, archetypes);
                if (duplicate) {
                    setError(`Ta lista kart już istnieje jako talia "${duplicate.name}". Nie możesz dodać duplikatu.`);
                    return;
                }
            }

            if (editingDeck) {
                // Edit existing deck
                await updateDeck(editingDeck.id, deckData);
                if (archetypeSuggestions[0]?.archetype?.id) {
                    await saveArchetypeSuggestionFeedback(editingDeck.id, {
                        suggestedArchetypeId: archetypeSuggestions[0].archetype.id,
                        chosenArchetypeId: deckData.archetype,
                        confidence: archetypeSuggestions[0].confidence || 0,
                        status: archetypeSuggestions[0].archetype.id === deckData.archetype ? 'accepted' : 'rejected'
                    });
                }
            } else {
                // Add new deck
                const createdDeckId = await createDeck(deckData);
                if (createdDeckId && archetypeSuggestions[0]?.archetype?.id) {
                    await saveArchetypeSuggestionFeedback(createdDeckId, {
                        suggestedArchetypeId: archetypeSuggestions[0].archetype.id,
                        chosenArchetypeId: deckData.archetype,
                        confidence: archetypeSuggestions[0].confidence || 0,
                        status: archetypeSuggestions[0].archetype.id === deckData.archetype ? 'accepted' : 'rejected'
                    });
                }
            }

            // Refresh decks list
            await refreshDecksList();

            // Wywołaj callback do odświeżenia statystyk
            if (onStatsUpdate) {
                onStatsUpdate();
            }

            handleCloseDialog();
        } catch (err) {
            console.error('Błąd podczas zapisywania talii:', err);
            setError(err.message || 'Nie udało się zapisać talii. Spróbuj ponownie.');
        }
    };

    const handleSuggestArchetype = async () => {
        if (!formData.cards?.trim()) {
            setError('Najpierw wklej listę kart, żeby wyliczyć sugestię.');
            return;
        }
        try {
            setSuggestingArchetype(true);
            const response = await suggestArchetypeForDeck({
                cards: formData.cards,
                format: trackerFilters?.format || formData.format,
                setId: trackerFilters?.setId || formData.setId,
                deckId: editingDeck?.id
            });
            setArchetypeSuggestions(response?.suggestions || []);
        } catch (err) {
            console.error('Błąd podczas sugerowania archetypu:', err);
            setError('Nie udało się pobrać sugestii archetypu.');
        } finally {
            setSuggestingArchetype(false);
        }
    };

    const handleApplySuggestion = (suggestion) => {
        if (!suggestion?.archetype?.id) return;
        const archetypeId = suggestion.archetype.id;
        setFormData((prev) => ({
            ...prev,
            archetype: archetypeId
        }));
        if (formData.cards?.trim()) {
            setNameSuggestions(suggestDeckNames(formData.cards, archetypeId, decks, archetypes));
        }
    };

    const handleSuggestName = () => {
        setSuggestingName(true);
        const suggestions = suggestDeckNames(formData.cards, formData.archetype, decks, archetypes);
        setNameSuggestions(suggestions);
        setSuggestingName(false);
    };

    const handleApplyNameSuggestion = (name) => {
        setFormData((prev) => ({ ...prev, name }));
    };

    const handleDeleteDeck = async (deckId) => {
        if (!currentUser) return;

        try {
            setError(null);

            // Znajdź talię, aby pobrać ścieżkę do zdjęcia
            const deckToDelete = decks.find(deck => deck.id === deckId);

            // Usuń talię z bazy danych
            await deleteDeck(deckId);

            // Usuń zdjęcie z Storage (jeśli istnieje)
            if (deckToDelete?.image_url) {
                // Wyciągnij ścieżkę z URL (część po ostatnim /)
                const urlParts = deckToDelete.image_url.split('/');
                const fileName = urlParts[urlParts.length - 1];
                const filePath = `deck-images/${currentUser.uid}/${fileName}`;
                await deleteDeckImage(filePath);
            }

            // Refresh decks list
            await refreshDecksList();

            // Wywołaj callback do odświeżenia statystyk
            if (onStatsUpdate) {
                onStatsUpdate();
            }
        } catch (err) {
            console.error('Błąd podczas usuwania talii:', err);
            setError(err.message || 'Nie udało się usunąć talii. Spróbuj ponownie.');
        }
    };

    const requestDeleteDeck = (deckId) => {
        const deck = decks.find((entry) => entry.id === deckId);
        setConfirmDeleteDeck({
            open: true,
            deckId,
            deckName: deck?.name || 'talię'
        });
    };

    const refreshDecksList = async () => {
        try {
            const [userDecksData, internalDecksData] = await Promise.all([
                currentUser ? getDecksWithArchetypes(currentUser.uid, trackerFilters) : [],
                getDecksWithArchetypes(null, trackerFilters)
            ]);

            const allDecks = [...userDecksData];
            const userDeckIds = new Set(userDecksData.map(deck => deck.id));

            internalDecksData.forEach(internalDeck => {
                if (!userDeckIds.has(internalDeck.id)) {
                    allDecks.push(internalDeck);
                }
            });

            setDecks(allDecks);
        } catch (err) {
            console.error('Błąd podczas odświeżania listy talii:', err);
        }
    };

    const handleToggleFavorite = async (deckId) => {
        if (!currentUser) return;

        try {
            const newFavorites = new Set(userFavoriteDecks);
            if (newFavorites.has(deckId)) {
                newFavorites.delete(deckId);
            } else {
                newFavorites.add(deckId);
            }

            setUserFavoriteDecks(newFavorites);

            // Zapisz w bazie danych
            await createOrUpdateUser(currentUser.uid, {
                favoriteDecks: Array.from(newFavorites)
            });
        } catch (err) {
            console.error('Błąd podczas aktualizacji ulubionych:', err);
        }
    };

    const handleCategoryChange = async (deckId, category) => {
        if (!currentUser) return;

        try {
            const newCategories = { ...userDeckCategories };
            newCategories[deckId] = category;
            setUserDeckCategories(newCategories);

            // Zapisz w bazie danych
            await createOrUpdateUser(currentUser.uid, {
                deckCategories: newCategories
            });
        } catch (err) {
            console.error('Błąd podczas aktualizacji kategorii:', err);
        }
    };

    const handleCopyDeckList = (deck) => {
        if (deck.cards) {
            navigator.clipboard.writeText(deck.cards);
        }
    };

    const handleImageClick = (deck) => {
        setPreviewDeck(deck);
    };

    const handleToggleArchetype = (archetypeId) => {
        const newExpanded = new Set(expandedArchetypes);
        if (newExpanded.has(archetypeId)) {
            newExpanded.delete(archetypeId);
        } else {
            newExpanded.add(archetypeId);
        }
        setExpandedArchetypes(newExpanded);
    };

    // Funkcje pomocnicze
    const isDeckFavorite = (deckId) => {
        return userFavoriteDecks.has(deckId);
    };

    const getUserDeckCategory = (deckId) => {
        return userDeckCategories[deckId] || 'unknown';
    };

    const getCategoryColor = (category) => {
        const colors = {
            unknown: '#6b7280',
            to_test: '#f59e0b',
            rejected: '#ef4444',
            selected: '#10b981'
        };
        return colors[category] || colors.unknown;
    };

    const getCategoryLabel = (category) => {
        const labels = {
            unknown: 'Nieznana',
            to_test: 'Do przetestowania',
            rejected: 'Odrzucona',
            selected: 'Wybrana'
        };
        return labels[category] || labels.unknown;
    };

    // Filtrowanie i grupowanie talii
    const filteredDecks = decks.filter(deck => {
        // Filtruj po nazwie talii lub nazwach kart
        if (searchQuery) {
            const query = normalizeSearchText(searchQuery);
            const haystack = buildDeckSearchHaystack(deck);
            if (!query || !haystack.includes(query)) {
                return false;
            }
        }

        // Filtruj po archetypie
        if (archetypeFilter && deck.archetype !== archetypeFilter) {
            return false;
        }

        // Filtruj po kategorii użytkownika
        if (categoryFilter && getUserDeckCategory(deck.id) !== categoryFilter) {
            return false;
        }

        // Filtruj po ulubionych
        if (showFavoritesOnly && !isDeckFavorite(deck.id)) {
            return false;
        }

        // Filtruj po moich taliach
        if (showMyDecksOnly && deck.author !== currentUser?.uid) {
            return false;
        }

        return true;
    });

    // Grupowanie po archetypie
    const groupedDecks = (() => {
        const groups = {};

        filteredDecks.forEach(deck => {
            const archetypeId = deck.archetype || 'no-archetype';
            const archetype = archetypes.find(a => a.id === archetypeId);

            if (!groups[archetypeId]) {
                groups[archetypeId] = {
                    archetypeId,
                    name: archetype ? archetype.name : 'Brak archetypu',
                    archetype,
                    decks: []
                };
            }

            groups[archetypeId].decks.push(deck);
        });

        // Sortuj talie w każdym archetypie według liczby gier malejąco
        Object.values(groups).forEach(group => {
            group.decks.sort((a, b) => {
                const gamesA = a.stats?.overall?.games || 0;
                const gamesB = b.stats?.overall?.games || 0;
                return gamesB - gamesA; // Malejąco
            });
        });

        // Sortuj archetypy według łącznej liczby gier malejąco
        return Object.values(groups).sort((a, b) => {
            const totalGamesA = a.decks.reduce((sum, deck) => sum + (deck.stats?.overall?.games || 0), 0);
            const totalGamesB = b.decks.reduce((sum, deck) => sum + (deck.stats?.overall?.games || 0), 0);
            return totalGamesB - totalGamesA; // Malejąco
        });
    })();

    if (!currentUser) {
        return (
            <Box>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Musisz być zalogowany, aby zarządzać taliami.
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: '#7c3aed' }} />
            </Box>
        );
    }

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {decks.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <DeckIcon sx={{ fontSize: 64, color: 'rgba(196, 181, 253, 0.3)', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                        Brak zarejestrowanych talii
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mb: 3 }}>
                        Dodaj swoją pierwszą talię aby rozpocząć śledzenie wyników
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Dodaj Talię
                    </Button>
                </Box>
            ) : (
                <Box>
                    {/* Informacja o taliach */}
                    <Box sx={{
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '10px',
                        p: 2,
                        mb: 2
                    }}>
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.82)', mb: 0.5, fontWeight: 600 }}>
                            Biblioteka talii społeczności
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)' }}>
                            Przed dodaniem nowej listy sprawdź, czy już istnieje. Podgląd kart tworzy się automatycznie z listy kart.
                        </Typography>
                    </Box>

                    {/* Filtry */}
                    <DeckFilters
                        archetypeFilter={archetypeFilter}
                        setArchetypeFilter={setArchetypeFilter}
                        categoryFilter={categoryFilter}
                        setCategoryFilter={setCategoryFilter}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        showFavoritesOnly={showFavoritesOnly}
                        setShowFavoritesOnly={setShowFavoritesOnly}
                        showMyDecksOnly={showMyDecksOnly}
                        setShowMyDecksOnly={setShowMyDecksOnly}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        archetypes={archetypes}
                        currentUser={currentUser}
                    />

                    {/* Lista talii */}
                    <DeckGroupedList
                        groupedDecks={groupedDecks}
                        expandedArchetypes={expandedArchetypes}
                        onToggleArchetype={handleToggleArchetype}
                        viewMode={viewMode}
                        currentUser={currentUser}
                        isDeckFavorite={isDeckFavorite}
                        getUserDeckCategory={getUserDeckCategory}
                        getCategoryColor={getCategoryColor}
                        getCategoryLabel={getCategoryLabel}
                        onToggleFavorite={handleToggleFavorite}
                        onCopyDeckList={handleCopyDeckList}
                        onEditDeck={handleOpenDialog}
                        onDeleteDeck={requestDeleteDeck}
                        onCategoryChange={handleCategoryChange}
                        onImageClick={handleImageClick}
                        onOpenQuickMatch={onOpenQuickMatch}
                    />
                </Box>
            )}

            {/* Formularz dodawania/edycji talii */}
            <DeckForm
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleSubmit}
                editingDeck={editingDeck}
                archetypes={archetypes}
                formData={formData}
                onInputChange={handleInputChange}
                trackerContext={{
                    format: trackerFilters?.format || 'core_constructed',
                    formatLabel: FORMAT_LABELS[trackerFilters?.format || 'core_constructed'] || 'Core Constructed',
                    setId: trackerFilters?.setId || '',
                    setLabel: trackerSetLabel || trackerFilters?.setId || ''
                }}
                suggestingArchetype={suggestingArchetype}
                archetypeSuggestions={archetypeSuggestions}
                onSuggestArchetype={handleSuggestArchetype}
                onApplySuggestion={handleApplySuggestion}
                suggestingName={suggestingName}
                nameSuggestions={nameSuggestions}
                onSuggestName={handleSuggestName}
                onApplyNameSuggestion={handleApplyNameSuggestion}
            />

            <ConfirmDialog
                open={confirmDeleteDeck.open}
                title="Usunąć talię?"
                message={`Czy na pewno chcesz usunąć talię "${confirmDeleteDeck.deckName}"? Tej operacji nie można cofnąć.`}
                confirmLabel="Usuń talię"
                onClose={() => setConfirmDeleteDeck({ open: false, deckId: null, deckName: '' })}
                onConfirm={async () => {
                    if (confirmDeleteDeck.deckId) {
                        await handleDeleteDeck(confirmDeleteDeck.deckId);
                    }
                    setConfirmDeleteDeck({ open: false, deckId: null, deckName: '' });
                }}
            />

            <DeckPreviewModal
                open={Boolean(previewDeck)}
                onClose={() => setPreviewDeck(null)}
                deck={previewDeck}
            />
        </Box>
    );
}

export default DeckManager;
